import React, { useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useNavigationPage from '../../hooks/useNavigationPage';
import useBatchService from '../../app/services/hooks/useBatchService';
import { BatchDataForm, BatchFilters } from '../../app/models/Batch';
import BatchDataTable from './components/tables/BatchDataTable';
import DefaultModal from '../../components/default/DefaultModal';
import BatchCreate from './components/BatchCreate';

const breadcrumbs: BreadcrumbsItem[] = [
  { name: 'Lotes Recolección', url: '/batches/collection', isActive: true }
];

const BatchesCollection = () => {
  const { navigationPage } = useNavigationPage();
  const { fetchingGetBatches, getBatches, deleteBatch, cloneBatch } = useBatchService();

  const [batches, setBatches] = useState<BatchDataForm[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [showingCreate, setShowingCreate] = useState(false);

  const [batchFilter, setBatchFilter] = useState<BatchFilters>({
    page: 1,
    per_page: 10,
    sort: 'id',
    order: 'desc',
    batch_category: 'COLLECTION'
  });

  useEffect(() => {
    getBatches(batchFilter, {
      onSuccess: (response: ServiceResponse) => {
        setBatches(response.data.batches);
        setTotalRows(response.data.total_rows);
      },
      onError: (response: ServiceResponse) => {
        toast.error(response.message, { autoClose: 2000 });
        if (response.data?.to_dashboard) navigationPage('/');
      }
    });
  }, [batchFilter.page, batchFilter.per_page, batchFilter.sort, batchFilter.order]);

  const reloadTable = () => {
    getBatches(batchFilter, {
      onSuccess: (response: ServiceResponse) => {
        setBatches(response.data.batches);
        setTotalRows(response.data.total_rows);
      },
      onError: (response: ServiceResponse) => {
        toast.error(response.message, { autoClose: 2000 });
        if (response.data?.to_dashboard) navigationPage('/');
      }
    });
  };

  const showEdit = (id: number) => {
    navigationPage(`/batch/${id}/edit`);
  };

  const show = (id: number) => {
    navigationPage(`/batch/${id}/show`);
  };

  const showCreate = () => setShowingCreate(true);
  const hideCreate = () => setShowingCreate(false);

  const destroy = (id: number) => {
    const _text = 'Está a punto de eliminar el lote #' + id;
    useReactConfirmAlert().requestConfirmation({
      title: '¿Estás seguro?',
      message: _text,
      buttons: {
        confirmButton: {
          label: 'Si, confirmar',
          onClick: () => {
            deleteBatch(id, {
              onSuccess: (response: ServiceResponse) => {
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                reloadTable();
              },
              onError: (response: ServiceResponse) => {
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
              }
            });
          }
        },
        cancelButton: {
          label: 'No, cancelar',
          onClick: () => {
            setTimeout(() => {
              useReactConfirmAlert().infoAlert({ title: 'Cancelado', message: 'El lote no se ha eliminado.' });
            }, 0);
          }
        }
      }
    });
  };

  const handleClone = (id: number) => {
    const _text =
      'Está a punto de clonar el lote #' +
      id +
      '. Se creará un nuevo lote con los mismos datos pero con cantidad en uso = 0.';
    useReactConfirmAlert().requestConfirmation({
      title: '¿Clonar lote?',
      message: _text,
      buttons: {
        confirmButton: {
          label: 'Sí, clonar',
          onClick: () => {
            cloneBatch(id, {
              onSuccess: (response: ServiceResponse) => {
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                reloadTable();
              },
              onError: (response: ServiceResponse) => {
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
              }
            });
          }
        },
        cancelButton: {
          label: 'No, cancelar',
          onClick: () => {
            setTimeout(() => {
              useReactConfirmAlert().infoAlert({ title: 'Cancelado', message: 'El lote no se ha clonado.' });
            }, 0);
          }
        }
      }
    });
  };

  return (
    <>
      <Breadcrumbs pageSection="Lotes Recolección" breadcrumbs={breadcrumbs} />

      <DefaultCard>
        <DefaultToolBar left={<ButtonCreate callbackCreate={showCreate} title="Nuevo lote" />} />
        <BatchDataTable
          batches={batches}
          edit={showEdit}
          show={show}
          destroy={destroy}
          clone={handleClone}
          loading={fetchingGetBatches}
          filter={batchFilter}
          setFilter={setBatchFilter}
          totalRows={totalRows}
          callbackSuccess={reloadTable}
          onSearch={reloadTable}
          mode="collection"
        />
      </DefaultCard>

      {showingCreate && (
        <DefaultModal show={showingCreate} handleClose={hideCreate} title="Crear lote" backdrop={true} showFooter={false}>
          <BatchCreate onSaved={() => { reloadTable(); hideCreate(); }} onCancel={hideCreate} />
        </DefaultModal>
      )}
    </>
  );
};

export default BatchesCollection;

